import {
  Button,
  Checkbox,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Select,
  Spinner,
} from "@chakra-ui/react";
import { useCallback } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import useCreateOrEditLearningPath from "../../hooks/api/learning-path/useCreateOrEditLearningPath";
import useGetTeacherLanguages from "../../hooks/api/learning-path/useGetTeacherLanguages";
import { LearningPath } from "../../models/LearningPath";

type FormValues = {
  name: string;
  description: string;
  languageId: string;
  enabled: boolean;
};

type Props = {
  learningPath?: LearningPath;
  onSuccess?: (learningPathId: number) => void;
  onError?: () => void;
};

export default function LearningPathForm({
  learningPath,
  onSuccess,
  onError,
}: Props) {
  const { t } = useTranslation();
  const {
    handleSubmit,
    register,
    formState: { errors, isValid, isSubmitted },
  } = useForm<FormValues>({
    defaultValues: {
      name: learningPath?.name || "",
      description: learningPath?.description || "",
      languageId: learningPath?.language.id || "",
      enabled: !!learningPath?.enabled,
    },
  });

  const { data: languages, isLoading: areLanguagesLoading } =
    useGetTeacherLanguages();

  const { mutateAsync: createOrEditLearningPath, isPending } =
    useCreateOrEditLearningPath();

  const onSubmit = useCallback(
    (values: FormValues) => {
      createOrEditLearningPath({
        learningPath: { id: learningPath?.id, ...values },
        operation: learningPath ? "edit" : "create",
      })
        .then((response) => onSuccess?.(response.id))
        .catch(onError);
    },
    [createOrEditLearningPath, learningPath, onError, onSuccess],
  );

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <FormControl
        isInvalid={!isValid && isSubmitted}
        display="flex"
        flexDirection="column"
        gap={4}
      >
        <div>
          <FormLabel htmlFor="name">{t("forms.name")}</FormLabel>
          <Input
            id="name"
            placeholder={t("forms.name")}
            {...register("name", { required: t("forms.validation.required") })}
          />
          <FormErrorMessage>
            {errors.name && errors.name.message}
          </FormErrorMessage>
        </div>
        <div>
          <FormLabel htmlFor="description">{t("forms.description")}</FormLabel>
          <Input
            id="description"
            placeholder={t("forms.description")}
            {...register("description", {
              required: t("forms.validation.required"),
            })}
          />
          <FormErrorMessage>
            {errors.description && errors.description.message}
          </FormErrorMessage>
        </div>
        <div>
          <FormLabel htmlFor="languageId">{t("forms.language")}</FormLabel>
          {languages?.content.length ? (
            <Select
              id="languageId"
              {...register("languageId", {
                required: t("forms.validation.required"),
              })}
            >
              <option value="">-</option>
              {languages.content.map((language) => (
                <option key={language.id} value={language.id}>
                  {language.name}
                </option>
              ))}
            </Select>
          ) : (
            <Spinner color="primary.DEFAULT" />
          )}
          <FormErrorMessage>
            {errors.languageId && errors.languageId.message}
          </FormErrorMessage>
        </div>
        <div>
          <FormLabel htmlFor="enabled">{t("forms.state")}</FormLabel>
          <Checkbox id="enabled" {...register("enabled")}>
            {t("forms.enabled")}
          </Checkbox>
        </div>
      </FormControl>
      <Button
        mt={4}
        bg="primary.DEFAULT"
        color="white"
        disabled={areLanguagesLoading}
        isLoading={isPending}
        type="submit"
      >
        {t("common.buttons.save")}
      </Button>
    </form>
  );
}
